import React from 'react';
import styled from 'styled-components';
import { Box, Heading, Link, Paragraph, Stack, Text } from '@99designs/emu';
import { colors, spaces, typography } from '@99designs/emu/settings';
import { IconDesign1, IconFindDesigner1, IconProfile1, IconStopWatch2 } from '@99designs/icons';
import { profilePaths } from '../../../profileHelper';
import PillContainer from './PillContainer';
import { Designer } from './types';

const Panel = styled.div`
    display: flex;
    position: sticky;
    flex-direction: column;
    padding-bottom: ${spaces['1.5bu']};
    padding-left: ${spaces['2bu']};
    padding-right: ${spaces['2bu']};
`;

const DesignerIcon = styled(IconFindDesigner1)`
    height: 16px;
    width: 16px;
    color: ${colors.grey800};
    padding-right: ${spaces['0.25bu']};
`;

const ExperienceItem = styled(Box)`
    display: flex;
    align-items: center;
    gap: ${spaces['0.5bu']};
`;

export interface DesignerDetailsBodyProps {
    designer: Designer;
    pitch?: string;
    jobId?: string;
}

function DesignerDetailsBody(props: DesignerDetailsBodyProps) {
    const { designer, pitch, jobId } = props;
    const {
        biography,
        certifications,
        completedProjectsCount,
        repeatCount,
        responsivenessScore,
        locales,
        categories,
    } = props.designer.profile;

    const text: string = pitch || biography;
    const paths = profilePaths(designer.id, jobId);

    return (
        <Panel>
            <Stack space={'1.5bu'}>
                <Box>
                    <Box marginTop="1bu" marginBottom="0.5bu">
                        <Heading
                            color="grey800"
                            accessibilityLevel={6}
                            fontFamily="text"
                            appearanceLevel={6}
                            fontWeight={`${typography.fontWeights.bold}`}
                        >
                            {pitch ? 'Pitch' : 'About'}
                        </Heading>
                    </Box>

                    <Paragraph as="div" size="regular" data-qm-block>
                        {text}
                    </Paragraph>
                </Box>
                <Box marginBottom="0.5bu">
                    <Link href={paths.base} target="_blank" data-qm-block>
                        <DesignerIcon />
                        <Text size="size3">View Full Profile</Text>
                    </Link>
                </Box>
            </Stack>
            <Stack space={'0.5bu'}>
                {locales.length > 0 && (
                    <PillContainer
                        title={__('Languages')}
                        items={locales.map((locale) => locale.title)}
                    />
                )}
                {certifications.length > 0 && (
                    <PillContainer
                        title={__('Certifications')}
                        items={certifications.map((certification) => certification)}
                    />
                )}
                <Box paddingBottom={'0.25bu'}>
                    <Box marginTop="1bu" marginBottom="0.5bu">
                        <Heading
                            color="grey800"
                            accessibilityLevel={6}
                            fontFamily="text"
                            fontWeight={`${typography.fontWeights.bold}`}
                            appearanceLevel={6}
                        >
                            Experience
                        </Heading>
                    </Box>
                    <Stack space={'0.75bu'}>
                        <ExperienceItem>
                            <IconDesign1 /> <Text>{completedProjectsCount}: 1-to-1 Projects</Text>
                        </ExperienceItem>
                        <ExperienceItem>
                            <IconProfile1 /> <Text>{repeatCount}: Repeat Clients</Text>
                        </ExperienceItem>
                        <ExperienceItem>
                            <IconStopWatch2 /> <Text>{responsivenessScore}% Responses in 24hr</Text>
                        </ExperienceItem>
                    </Stack>
                </Box>
                {categories.length > 0 && (
                    <PillContainer
                        title={__('Categories')}
                        items={categories.map((category) => category.title)}
                    />
                )}
            </Stack>
        </Panel>
    );
}

export default DesignerDetailsBody;
